import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserOutlined } from "@ant-design/icons";
import { useStaffIdentity } from "../../../common/staff/StaffIdentityProvider";
import { Select } from "../../../ui/primitives/Select";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import { useStaffUserService } from "../../../services/members/StaffUserServiceProvider";
import { CurrentStaff, StaffUser } from "../../../model/members/StaffUser";

/**
 * "Who's on the desk" — the staff member this device is currently signed in as.
 *
 * Reception machines are shared, and plenty of staff have no email of their own, so the
 * login alone often can't say whose tasks, calls and Handover messages to show. Picking a
 * name here answers that for every CRM screen at once.
 *
 * It renders nothing at all when the login is already linked to a staff profile by email:
 * that identity is fixed and unspoofable, so offering to change it would be a lie.
 */
const StaffIdentityPicker: React.FC = () => {
  const staffUserService = useStaffUserService();
  const { selectedStaffId, selectStaff } = useStaffIdentity();

  const [current, setCurrent] = useState<CurrentStaff | null>(null);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const load = useCallback(async () => {
    // Both are best-effort: a staff account that can't list the roster should still get a
    // usable header rather than an error toast on every page.
    const [me, staff] = await Promise.all([
      staffUserService.getCurrentStaff().catch(() => null),
      staffUserService.getStaffUsers().catch(() => [] as StaffUser[]),
    ]);
    setCurrent(me);
    setStaffUsers(staff);
    setLoaded(true);

    // A selection can go stale — the person was deactivated, or removed from this club —
    // and would then silently 409 on every action. Clear it so the prompt comes back.
    //
    // Only on a *successful* answer of "nobody": `me == null` means the request itself
    // failed, and treating a deploy blip or dropped connection as "you're not signed in"
    // would log out every reception desk at once.
    if (me != null && me.staff == null) {
      selectStaff(null);
    }
  }, [staffUserService, selectStaff]);

  // Re-runs when the selection changes as well as on mount: after picking a name the API
  // resolves someone new, and the label has to follow ("Who are you?" → "You are").
  useEffect(() => {
    load();
  }, [load, selectedStaffId]);

  const options = useMemo(
    () => staffUsers.filter((s) => s.isActive).map((s) => ({ label: s.name, value: s.id })),
    [staffUsers]
  );

  // Identity comes from the login itself — nothing to choose.
  if (current?.fromLogin) return null;

  // Don't flash a "pick who you are" prompt before we know whether one is needed, and
  // don't nag when the API simply couldn't be reached — that's not the user's problem to
  // solve with a dropdown.
  if (!loaded || current == null) return null;

  return (
    // White rather than a token: this sits on the header, whose background is the tenant's
    // sidebar colour and always dark. The rest of the header (UserMenu, the Fortius Admin
    // title) hardcodes white on the same band for the same reason, and the secondary text
    // colour these primitives default to is unreadable against it.
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      style={{ color: "white" }}
      data-testid="staff-identity-picker"
    >
      <UserOutlined aria-hidden />
      <Text style={{ whiteSpace: "nowrap", color: "inherit" }}>
        {current.staff ? "You are" : "Who are you?"}
      </Text>
      <Select
        style={{ minWidth: 160 }}
        placeholder="Select your name"
        options={options}
        value={selectedStaffId ?? undefined}
        onChange={(value) => selectStaff(value == null ? null : Number(value))}
        data-testid="staff-identity-select"
      />
    </Stack>
  );
};

export default StaffIdentityPicker;
